import { useMemo } from 'react';
import { NJCheckbox, NJText } from '@engie-group/fluid-design-system-react';
import DataTable from '@shared/DataTable';
import type { CellClickedEvent, ColDef, ICellRendererParams } from 'ag-grid-community';
import { UMSelect } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { routeOf } from './construction';
import { segmentKm } from './sectionEngine';
import { YEARS } from './engine';
import type { Plan } from './types';

interface Row { index: number; label: string; year: string; mode: string; km: number }
export function PipePhaseTable({ plan, geo, canWrite, selected, onSelect, onYear }: {
  plan: Plan; geo?: PlanGeoData; canWrite: boolean; selected: number[];
  onSelect: (indexes: number[]) => void; onYear: (index: number, year: string) => void;
}) {
  const rows = useMemo(() => {
    const route = routeOf(plan, geo?.pipes.features.length ?? 0);
    return route.indexes.map((index): Row => ({
      index, label: `Tronçon ${index + 1}`, year: route.phases[index],
      mode: plan.pipePhaseOverrides?.[index] ? 'Manuel' : 'Automatique simulé', km: segmentKm(geo, index),
    }));
  }, [plan, geo]);
  const columns: ColDef<Row>[] = [
    { headerName: 'Choix', colId: 'choice', minWidth: 65, maxWidth: 75, filter: false, sortable: false,
      cellRenderer: ({ data }: ICellRendererParams<Row>) => data && <NJCheckbox label="" aria-label={`Choisir ${data.label}`}
        isChecked={selected.includes(data.index)} onChange={(_, checked) =>
          onSelect(checked ? [...new Set([...selected, data.index])] : selected.filter((index) => index !== data.index))} /> },
    { field: 'label', headerName: 'Canalisation', minWidth: 125 },
    { field: 'km', headerName: 'Longueur (km)', minWidth: 110, filter: 'agNumberColumnFilter',
      valueFormatter: ({ value }) => Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 3 }) },
    { field: 'mode', headerName: 'Mode', minWidth: 130 },
    { field: 'year', headerName: 'Année', minWidth: 110,
      cellRenderer: ({ data }: ICellRendererParams<Row>) => data && <fieldset disabled={!canWrite} style={{ border: 0, margin: 0, padding: 0 }}>
        <UMSelect value={data.year} ariaLabel={`Année du tronçon ${data.index + 1}`} onChange={(year) => onYear(data.index, year)}
          options={YEARS.map((year) => ({ value: year, label: year }))} />
      </fieldset> },
  ];
  return <section>
    <NJText scale="xs">Colonnes triables et filtrables. Défilement horizontal si nécessaire. Le choix d’une ligne met la canalisation en surbrillance sur la carte.</NJText>
    <div className="np-pipe-grid" style={{ height: 350, overflow: 'hidden' }}>
      <style>{'.np-pipe-grid > div { height: 100% !important; }'}</style>
      <DataTable data={rows} header={columns} testId="phase-pipes" verticalScroll
        defaultColDef={{ flex: 1, minWidth: 100, sortable: true, filter: true, floatingFilter: true, resizable: true }}
        onGridReady={({ api }) => api.addEventListener('cellClicked', ({ data, column }: CellClickedEvent<Row>) => {
          if (data && !['choice', 'year'].includes(column.getColId())) onSelect([data.index]);
        })} />
    </div>
    {!rows.length && <NJText scale="sm">Aucune canalisation : réalisez d’abord le tracé.</NJText>}
  </section>;
}
